import { Circle, Group, Line, Rect } from "react-konva";
import { PITCH_H, PITCH_W, type Dims } from "@/lib/canvas/coords";

/**
 * Soccer-pitch background, rendered directly via react-konva
 * primitives so the canvas stays a single layer and we don't have to
 * load an external SVG / image asset on boot.
 *
 * The stage (`dims`) is a 16:9 rectangle. The whole stage is filled
 * green; the pitch MARKINGS (touchlines, boxes, circle, …) are drawn
 * inside a centered sub-rectangle held at the true pitch proportion
 * (`PITCH_W : PITCH_H` ≈ 1.544 : 1). Since that's narrower than 16:9,
 * the inset is height-bound and leaves a green margin on each side —
 * so the board reads as "a scaled pitch on a green field", matching
 * the video stage's 16:9 footprint with no black letterbox bars.
 *
 * The pitch is drawn landscape, attacking left-to-right (matching
 * the legacy convention used by `FORMATION_PRESETS`). All
 * coordinates are in pixels.
 *
 * Caller is responsible for the `listening: true/false` hint — this
 * component is a pure decorative layer.
 */

export type PitchBackgroundProps = {
  dims: Dims;
  /** Optional override for the field color. Default matches the
   * legacy pitch fill (a deep green that reads on both dark + light
   * theme). */
  color?: string;
  /** Optional override for line color. */
  lineColor?: string;
};

const DEFAULT_FIELD = "#1f6f4a";
const DEFAULT_LINES = "rgba(255, 255, 255, 0.78)";
const PITCH_RATIO = PITCH_W / PITCH_H;

export function PitchBackground(props: PitchBackgroundProps) {
  const { dims } = props;
  const fill = props.color ?? DEFAULT_FIELD;
  const stroke = props.lineColor ?? DEFAULT_LINES;
  const { width: stageW, height: stageH } = dims;

  // The pitch-markings rectangle: the largest true-proportion
  // (PITCH_W:PITCH_H) box that fits the 16:9 stage, centered. The
  // stage is wider than the pitch ratio, so it's height-bound — a
  // small green margin sits on the left + right.
  const pad = 4;
  const fitH = stageH - pad * 2;
  const fitW = Math.min(stageW - pad * 2, fitH * PITCH_RATIO);
  const pitchH = fitW / PITCH_RATIO;
  // Origin of the inset pitch within the stage.
  const ox = (stageW - fitW) / 2;
  const oy = (stageH - pitchH) / 2;

  const innerW = fitW;
  const innerH = pitchH;
  const cx = ox + innerW / 2;
  const cy = oy + innerH / 2;
  const centreR = Math.min(innerW, innerH) * 0.097;

  // Penalty boxes
  const pBoxW = innerW * 0.165;
  const pBoxH = innerH * 0.394;
  const pBoxYOffset = (innerH - pBoxH) / 2;

  // 6-yard boxes
  const sBoxW = innerW * 0.055;
  const sBoxH = innerH * 0.198;
  const sBoxYOffset = (innerH - sBoxH) / 2;

  // Goal lines (thin notches outside the playing area, decorative).
  const goalW = innerW * 0.014;
  const goalH = innerH * 0.105;
  const goalYOffset = (innerH - goalH) / 2;

  return (
    <Group listening={false}>
      {/* Green field — fills the whole 16:9 stage. */}
      <Rect
        x={0}
        y={0}
        width={stageW}
        height={stageH}
        fill={fill}
        cornerRadius={6}
      />
      {/* Touchlines — the true-proportion pitch rectangle, inset. */}
      <Rect
        x={ox}
        y={oy}
        width={innerW}
        height={innerH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Halfway line */}
      <Line
        points={[cx, oy, cx, oy + innerH]}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Centre circle + spot */}
      <Circle x={cx} y={cy} radius={centreR} stroke={stroke} strokeWidth={2} />
      <Circle x={cx} y={cy} radius={3} fill={stroke} />
      {/* Left penalty box */}
      <Rect
        x={ox}
        y={oy + pBoxYOffset}
        width={pBoxW}
        height={pBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Right penalty box */}
      <Rect
        x={ox + innerW - pBoxW}
        y={oy + pBoxYOffset}
        width={pBoxW}
        height={pBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Left 6-yard box */}
      <Rect
        x={ox}
        y={oy + sBoxYOffset}
        width={sBoxW}
        height={sBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Right 6-yard box */}
      <Rect
        x={ox + innerW - sBoxW}
        y={oy + sBoxYOffset}
        width={sBoxW}
        height={sBoxH}
        stroke={stroke}
        strokeWidth={2}
      />
      {/* Penalty spots */}
      <Circle x={ox + innerW * 0.11} y={cy} radius={3} fill={stroke} />
      <Circle x={ox + innerW * 0.89} y={cy} radius={3} fill={stroke} />
      {/* Goals (decorative notches outside the touchlines) */}
      <Rect
        x={ox - goalW}
        y={oy + goalYOffset}
        width={goalW}
        height={goalH}
        stroke={stroke}
        strokeWidth={2}
      />
      <Rect
        x={ox + innerW}
        y={oy + goalYOffset}
        width={goalW}
        height={goalH}
        stroke={stroke}
        strokeWidth={2}
      />
    </Group>
  );
}
